import { createContext, ReactNode, useContext, useState } from 'react';

export type SummaryTableKey = 'court-cases' | 'defendants' | 'seizures' | 'hearings';

type TableDataDict = Partial<Record<SummaryTableKey, any[]>>;

interface SummaryTableDataContextValue {
    tableData: TableDataDict;
    setTableRows: (key: SummaryTableKey, rows: any[]) => void;
    appendTableRows: (key: SummaryTableKey, rows: any[]) => void;
    clearTableRows: (key: SummaryTableKey) => void;
}

const SummaryTableDataContext = createContext<SummaryTableDataContextValue | null>(null);

export const SummaryTableDataProvider = ({ children }: { children: ReactNode }) => {
    const [tableData, setTableData] = useState<TableDataDict>({});

    const setTableRows = (key: SummaryTableKey, rows: any[]) => {
        setTableData((prev) => ({ ...prev, [key]: rows }));
    };

    const appendTableRows = (key: SummaryTableKey, rows: any[]) => {
        setTableData((prev) => ({ ...prev, [key]: [...(prev[key] ?? []), ...rows] }));
    };

    const clearTableRows = (key: SummaryTableKey) => {
        setTableData((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    return (
        <SummaryTableDataContext.Provider value={{ tableData, setTableRows, appendTableRows, clearTableRows }}>
            {children}
        </SummaryTableDataContext.Provider>
    );
};

export const useSummaryTableData = () => {
    const ctx = useContext(SummaryTableDataContext);
    if (!ctx) throw new Error('useSummaryTableData must be used within SummaryTableDataProvider');
    return ctx;
};
